// React Dependencies
import React, { useRef, useState, useEffect } from 'react';

// External Dependencies
import _ from 'lodash';
import PropTypes from 'prop-types';
import MaterialTable from 'material-table';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { connect } from 'react-redux';
import { TextField, Chip, FormControl, Typography, Button } from '@material-ui/core';
import { createMuiTheme, ThemeProvider } from '@material-ui/core/styles';
import { Modal, ModalHeader, ModalBody } from 'reactstrap';
import { toast } from 'react-toastify';

// Internal Dependencies
import MasterNameForm from './MasterNameForm';

import useDebounce from '../../global/use-debounce';
import OverLoading from '../../global/OverLoading';
import { Sleep } from '../../../common/util';
import { IsString } from '../../../common/checks';

import apiService from '../../../services.js/apiService';
import Colours from '../../../styles/colours';

import { getPaginateMasterNames, addMasterName, deleteMasterName, updateMasterName } from '../../../actions/admin/masterNameAction';
import { updateModalContent, toggleModal } from '../../../actions/modal';

const themeTable = createMuiTheme({
  overrides: {
    MuiTableRow: {
      root: {
        '&:hover, &:nth-child(odd):hover': {
          transition:      '0.3s ease',
          backgroundColor: '#ffecb3',
        },
        '&:nth-child(odd)': {
          backgroundColor: '#eeeeee',
        },
      },
    },
  },
});

const initializeFocusValue = {
  searchFocus: false,
};

const MasterNamesTable = (props) => {
  const tableRef = useRef(null);

  const [selectedMasterName, setSelectedMasterName] = useState(null);
  const [pageSize, setPageSize] = useState(20);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showLoader, setShowLoader] = useState(false);

  const [masterName, setMasterName] = useState('');
  const [locationName, setLocationName] = useState('');
  const [autoFocus, setAutoFocus] = useState(initializeFocusValue);
  const { masterNameFocus, locationNameFocus } = autoFocus;

  const debounceMasterName = useDebounce(masterName, 500);
  const debounceLocationName = useDebounce(locationName, 500);

  const [onResetMasterName, setOnResetMasterName] = useState(false);
  const [onResetLocationName, setOnResetLocationName] = useState(false);

  const columns = [
    { title: 'Master Name', field: 'masterName', filtering: false, width: '5%', render: (review) => review.masterName || '--' },
    {
      title:     'Location Name',
      field:     'Location Name',
      filtering: false,
      width:     '5%',
      cellStyle: {
        minWidth: 120,
      },
      render: (masterNameData) => (
        <Autocomplete
          disabled
          limitTags={3}
          multiple
          disableCloseOnSelect
          disablePortal
          id="review-location-names-outlined"
          options={masterNameData.names}
          value={masterNameData.names}
          renderTags={(value, getTagProps) => value.map((tag, index) => (
            <Chip
              {...getTagProps({ index })}
              key={tag || `tag-${index}`}
              variant="outlined"
              label={tag}
              className="mr-2 mb-2"
              style={{ borderColor: Colours.Black, fontSize: 12, background: Colours.Black, color: Colours.White }}
            />
          ))}
          renderInput={(params) => (
            <TextField {...params} variant="standard" />
          )}
        />
      ),
    },
  ];

  const closeModalHandler = () => {
    setIsModalOpen(false);
    setSelectedMasterName(null);
  };

  const addMasterNameHandler = async (data) => {
    try {
      setShowLoader(true);
      await props.addMasterName(data);
      toast.success('Master name created');
      closeModalHandler();

      await Sleep(100);
      tableRef.current.onQueryChange();
    } catch (err) {
      toast.error(IsString(err) ? err : err.mesage);
    } finally {
      setShowLoader(false);
    }
  };

  const updateMasterNameHandler = async (data) => {
    try {
      setShowLoader(true);
      await props.updateMasterName(data);
      toast.success('Master name updated');
      closeModalHandler();

      await Sleep(100);
      tableRef.current.onQueryChange();
    } catch (err) {
      toast.error(IsString(err) ? err : err.mesage);
    } finally {
      setShowLoader(false);
    }
  };

  const onEditHandler = (rowData) => {
    setSelectedMasterName(rowData);
    setIsModalOpen(true);
  };

  const onDeleteHandler = (masterNameId) => {
    props.updateModalContent({
      title:        'Are you sure?',
      content:      'All data related to this master name will be deleted!',
      actionButton: {
        actionText: 'Yes, Delete',
        actionType: 'danger',
        action:     async () => {
          await props.deleteMasterName(masterNameId);
          tableRef.current.onQueryChange();
        },
      },
      cancelText:       'No',
      closeAfterAction: true,
    });

    props.toggleModal();
  };

  const onSearchChangeHandler = (e) => {
    const { value, name } = e.target;

    if (name === 'filter-master-name') {
      setAutoFocus({ masterNameFocus: true, locationNameFocus: false });
      setMasterName(value);
    } else if (name === 'filter-location-name') {
      setAutoFocus({ locationNameFocus: true, masterNameFocus: false });
      setLocationName(value);
    }
  };

  const onClearFilterHandler = () => {
    if (tableRef.current) {
      setMasterName('');
      setLocationName('');
      setOnResetMasterName(true);
      setOnResetLocationName(true);

      // refresh table
      tableRef.current.onQueryChange();
    }
  };

  useEffect(() => {
    if (!onResetMasterName) {
      tableRef.current.onQueryChange();
    } else {
      setOnResetMasterName(false);
    }
  }, [debounceMasterName]);

  useEffect(() => {
    if (!onResetLocationName) {
      tableRef.current.onQueryChange();
    } else {
      setOnResetLocationName(false);
    }
  }, [debounceLocationName]);

  return (
    <div>
      <div className="col-12">
        <div className="d-flex align-items-center mt-5">
          <Typography className="mx-3">Master names and their custom review location names</Typography>
          <Button variant="contained" color="primary" onClick={() => setIsModalOpen(true)}>Add</Button>
        </div>
        <ThemeProvider theme={themeTable}>
          <MaterialTable
            tableRef={tableRef}
            className="material-table"
            title=""
            columns={columns}
            onChangeRowsPerPage={(size) => {
              setAutoFocus(initializeFocusValue);
              setPageSize(size);
            }}
            data={async (query) => {
              try {
                query.masterName = masterName;
                query.name = locationName;

                const result = await props.getPaginateMasterNames(query);

                let { rows, count } = result;
                let { page } = query;

                if (rows.length === 0) {
                  page = 0;

                  if (count !== 0) {
                    const resultFirstPage = await apiService.getMasterNames({ ...query, page });
                    rows = resultFirstPage.rows;
                  }
                }

                const formattedMasterNames = _.map(rows, (row) => ({ ...row, names: _.uniq(row.names) }));

                return {
                  data:       formattedMasterNames,
                  page,
                  totalCount: count,
                };
              } catch (err) {
                return {
                  data:       [],
                  page:       0,
                  totalCount: 0,
                };
              }
            }}
            actions={[
              {
                icon:    'edit',
                tooltip: 'Edit Review',
                onClick: (event, rowData) => onEditHandler(rowData),
              },
              {
                icon:    'delete',
                tooltip: 'Delete Review',
                onClick: (event, rowData) => onDeleteHandler(rowData.masterName),
              },
            ]}
            options={{
              pageSize,
              actionsColumnIndex:  -1,
              emptyRowsWhenPaging: false,
              filtering:           false,
              search:              false,
              sorting:             false,
            }}
            components={{
              Toolbar: () => (
                <div className="my-4 py-4">
                  <div className="row mx-1">
                    <div className="col-md-2">
                      <FormControl fullWidth>
                        <TextField
                          label="Master Name"
                          type="text"
                          name="filter-master-name"
                          value={masterName || ''}
                          onChange={onSearchChangeHandler}
                          autoFocus={masterNameFocus}
                        />
                      </FormControl>
                    </div>

                    <div className="col-md-2">
                      <FormControl fullWidth>
                        <TextField
                          label="Location Name"
                          type="text"
                          name="filter-location-name"
                          value={locationName || ''}
                          onChange={onSearchChangeHandler}
                          autoFocus={locationNameFocus}
                        />
                      </FormControl>
                    </div>
                    <div className="col-md-6" />
                    <div className="col-md-2">
                      <FormControl fullWidth>
                        <Button variant="contained" color="secondary" onClick={onClearFilterHandler}>Clear Filters</Button>
                      </FormControl>
                    </div>
                  </div>
                </div>
              ),
            }}
          />
        </ThemeProvider>
      </div>

      <div className="position-relative">
        <Modal
          isOpen={isModalOpen}
          toggle={() => closeModalHandler()}
          size="lg"
          autoFocus={false}
        >
          <ModalHeader toggle={() => closeModalHandler()}>
            {selectedMasterName ? 'Edit' : 'Add'}
            {' '}
            Master Name
          </ModalHeader>
          <ModalBody>
            <MasterNameForm
              selected={selectedMasterName}
              onAdd={addMasterNameHandler}
              onUpdate={updateMasterNameHandler}
            />
          </ModalBody>

          { showLoader && <OverLoading />}
        </Modal>
      </div>
    </div>
  );
};

MasterNamesTable.propTypes = {
  getPaginateMasterNames: PropTypes.func.isRequired,
  addMasterName:          PropTypes.func.isRequired,
  deleteMasterName:       PropTypes.func.isRequired,
  updateMasterName:       PropTypes.func.isRequired,
  updateModalContent:     PropTypes.func.isRequired,
  toggleModal:            PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  store: state.admin.masterName,
});

export default connect(mapStateToProps, {
  getPaginateMasterNames,
  addMasterName,
  deleteMasterName,
  updateMasterName,
  updateModalContent,
  toggleModal,
})(MasterNamesTable);
